import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

// Component for secure resume downloads
const ResumeDownloadButton = ({ resumeUrl, resumeFilename }) => {
  const [loading, setLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const handleViewResume = async () => {
    try {
      setLoading(true);
      setViewError("");

      // Extract file path from the resume URL for private bucket access
      const urlParts = resumeUrl.split("/");
      const fileName = urlParts[urlParts.length - 1];

      // Generate a signed URL for secure viewing (valid for 1 hour)
      const { data, error } = await supabase.storage
        .from("resumes")
        .createSignedUrl(fileName, 3600); // 1 hour expiry

      if (error) {
        throw error;
      }

      // Open in new tab for viewing
      window.open(data.signedUrl, "_blank");
    } catch (error) {
      setViewError("Failed to open resume. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleViewResume}
        disabled={loading}
        className={`inline-flex items-center px-4 py-2 rounded-md transition-colors duration-200 ${
          loading
            ? "bg-gray-400 cursor-not-allowed text-white"
            : "bg-aifund text-white hover:bg-aifund-dark"
        }`}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Opening...
          </>
        ) : (
          <>📄 View Resume ({resumeFilename})</>
        )}
      </button>

      {viewError && (
        <p className="text-red-600 dark:text-red-400 text-sm mt-2">
          {viewError}
        </p>
      )}
    </div>
  );
};

// Final Decision Confirmation Modal Component
const FinalDecisionModal = ({
  isOpen,
  onClose,
  onConfirm,
  decision,
  applicantName,
}) => {
  if (!isOpen) return null;

  const isFinalAccept = decision === "final_accept";
  const actionText = isFinalAccept ? "Final Accept" : "Final Reject";
  const colorClass = isFinalAccept ? "text-green-600" : "text-red-600";
  const buttonClass = isFinalAccept
    ? "bg-green-600 hover:bg-green-700"
    : "bg-red-600 hover:bg-red-700";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Confirm Final Decision
        </h3>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Are you sure you want to{" "}
          <span className={`font-semibold ${colorClass}`}>
            {actionText.toLowerCase()}
          </span>{" "}
          {applicantName}?
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-400 mt-2 block">
            This is a final decision and should be carefully considered.
          </span>
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-white rounded-md transition-colors duration-200 ${buttonClass}`}
          >
            Confirm {actionText}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminApplicant = () => {
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [adminDecisions, setAdminDecisions] = useState([]);
  const [finalDecisions, setFinalDecisions] = useState([]);
  const [finalDecision, setFinalDecision] = useState("");
  const [globalFinalDecision, setGlobalFinalDecision] = useState("");
  const [finalDecisionMadeBy, setFinalDecisionMadeBy] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFinalDecision, setPendingFinalDecision] = useState("");
  const [applicantsList, setApplicantsList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNavContext, setHasNavContext] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    checkAuth();
    fetchApplicant();
    fetchAdminDecisions();
    
    // Check if we have navigation context
    const fromList = searchParams.get("fromList") === "true";
    const currentIdx = parseInt(searchParams.get("currentIndex") || "0");
    const total = parseInt(searchParams.get("totalCount") || "0");
    
    if (fromList && total > 0) {
      setHasNavContext(true);
      setCurrentIndex(currentIdx);
      setTotalCount(total);
      fetchApplicantsList();
    }
  }, [id]);

  useEffect(() => {
    if (user) {
      fetchFinalDecisions();
    }
  }, [user, id]);

  const checkAuth = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/admin/login");
        return;
      }
      setUser(user);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchApplicant = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("applicants")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        setError("Applicant not found");
        return;
      }

      setApplicant(data);
    } catch (error) {
      setError(`Failed to load applicant: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminDecisions = async () => {
    try {
      const { data, error } = await supabase
        .from("admin_decisions")
        .select("*")
        .eq("applicant_id", id);

      if (error) {
        return;
      }

      setAdminDecisions(data || []);
    } catch (error) {
    }
  };

  const fetchApplicantsList = async () => {
    try {
      // Get all applicants first
      const { data: applicantsData, error: applicantsError } = await supabase
        .from("applicants")
        .select("*")
        .order("created_at", { ascending: false });

      if (applicantsError) {
        return;
      }

      // Fetch final decisions
      const { data: finalDecisionsData, error: finalDecisionsError } = await supabase
        .from("final_decision")
        .select("applicant_id, decision");

      if (finalDecisionsError) {
        return;
      }

      // Create a map of final decisions by applicant_id
      const finalDecisionsMap = {};
      (finalDecisionsData || []).forEach(decision => {
        finalDecisionsMap[decision.applicant_id] = decision.decision;
      });

      // Add final decision status to each applicant
      const applicantsWithFinalDecision = (applicantsData || []).map(applicant => ({
        ...applicant,
        final_decision_status: finalDecisionsMap[applicant.id] || null
      }));

      // Apply the same filtering logic as AdminDashboard
      const statusFilter = searchParams.get("statusFilter") || "all";
      const experienceFilter = searchParams.get("experienceFilter") || "all";
      const backgroundFilter = searchParams.get("backgroundFilter") || "all";
      const selectedGoals = searchParams.get("selectedGoals") ? searchParams.get("selectedGoals").split(",") : [];
      const searchQuery = searchParams.get("searchQuery") || "";
      const heardAboutFilter = searchParams.get("heardAboutFilter") || "all";

      const availableGoals = [
        "Getting a job opportunity",
        "Networking with professionals",
        "Learning new AI skills",
        "Building a product/project",
        "Meeting potential co-founders",
        "Winning prize money",
        "Earning recognition for innovation",
        "Gaining experience in competitions",
        "Contributing to open source",
        "Personal portfolio development",
        "Meeting industry mentors",
        "Other"
      ];

      const filteredApplicants = applicantsWithFinalDecision.filter((app) => {
        const matchesStatus = statusFilter === "all" || 
          app.status === statusFilter ||
          (statusFilter === "final_accept" && app.final_decision_status === "final_accept") ||
          (statusFilter === "final_reject" && app.final_decision_status === "final_reject");
        const matchesExperience = experienceFilter === "all" || app.professional_experience === `${experienceFilter} years`;
        const matchesBackground = backgroundFilter === "all" || app.professional_background?.includes(backgroundFilter);
        const matchesSearch = searchQuery === "" || app.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesHeardAbout = heardAboutFilter === "all" || app.heard_about_event === heardAboutFilter;
        
        const matchesGoals = selectedGoals.length === 0 || selectedGoals.length === availableGoals.length || (() => {
          if (!app.goals) return false;
          const applicantGoals = app.goals.split(',').map(goal => goal.trim());
          return applicantGoals.some(applicantGoal => 
            selectedGoals.some(selectedGoal => 
              applicantGoal.toLowerCase().includes(selectedGoal.toLowerCase()) ||
              selectedGoal.toLowerCase().includes(applicantGoal.toLowerCase())
            )
          );
        })();

        return matchesStatus && matchesExperience && matchesBackground && matchesSearch && matchesHeardAbout && matchesGoals;
      });

      setApplicantsList(filteredApplicants);
    } catch (error) {
    }
  };

  const fetchFinalDecisions = async () => {
    try {
      const { data, error } = await supabase
        .from("final_decision")
        .select("*")
        .eq("applicant_id", id);

      if (error) {
        return;
      }

      setFinalDecisions(data || []);

      // Check if ANY admin has made a final decision
      if (data && data.length > 0) {
        const anyFinalDecision = data[0]; // Take the first (and should be only) final decision
        setGlobalFinalDecision(anyFinalDecision.decision);
        setFinalDecisionMadeBy(anyFinalDecision.admin_email);
      } else {
        setGlobalFinalDecision("");
        setFinalDecisionMadeBy("");
      }

      // Set current user's decision for display purposes
      if (user?.email) {
        const currentUserDecision = data?.find(
          (decision) => decision.admin_email === user.email
        );
        if (currentUserDecision) {
          setFinalDecision(currentUserDecision.decision);
        } else {
          setFinalDecision("");
        }
      }
    } catch (error) {
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/admin/login");
    } catch (error) {
    }
  };

  // Navigation functions
  const navigateToNext = () => {
    if (currentIndex < applicantsList.length - 1) {
      const nextApplicant = applicantsList[currentIndex + 1];
      const newParams = new URLSearchParams(searchParams);
      newParams.set("currentIndex", (currentIndex + 1).toString());
      navigate(`/admin/applicant/${nextApplicant.id}?${newParams.toString()}`);
    }
  };

  const navigateToPrevious = () => {
    if (currentIndex > 0) {
      const prevApplicant = applicantsList[currentIndex - 1];
      const newParams = new URLSearchParams(searchParams);
      newParams.set("currentIndex", (currentIndex - 1).toString());
      navigate(`/admin/applicant/${prevApplicant.id}?${newParams.toString()}`);
    }
  };

  const canNavigateNext = hasNavContext && currentIndex < totalCount - 1;
  const canNavigatePrevious = hasNavContext && currentIndex > 0;

  // Smart dashboard navigation
  const navigateToDashboard = () => {
    if (hasNavContext) {
      // If we have navigation context, go to dashboard with preserved filters
      const dashboardParams = new URLSearchParams(searchParams);
      // Remove navigation-specific parameters
      dashboardParams.delete("fromList");
      dashboardParams.delete("currentIndex");
      dashboardParams.delete("totalCount");
      
      const queryString = dashboardParams.toString();
      const dashboardUrl = queryString ? `/admin/dashboard?${queryString}` : '/admin/dashboard';
      navigate(dashboardUrl);
    } else {
      // If no navigation context, use browser history (backward compatibility)
      navigate(-1);
    }
  };

  const updateApplicantStatus = async (newStatus) => {
    try {
      setUpdating(true);

      // Update applicant status
      const { error } = await supabase
        .from("applicants")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;

      // Record admin decision (upsert to replace existing decision)
      const { error: decisionError } = await supabase
        .from("admin_decisions")
        .upsert(
          {
            applicant_id: id,
            admin_email: user.email,
            decision: newStatus,
          },
          {
            onConflict: "applicant_id,admin_email",
          }
        );

      if (decisionError) {
      }

      // Update local state
      setApplicant((prev) => ({ ...prev, status: newStatus }));

      // Refresh admin decisions
      fetchAdminDecisions();
    } catch (error) {
      setError("Failed to update status");
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmFinalDecision = async () => {
    try {
      setUpdating(true);

      // Record final decision
      const { data, error: finalDecisionError } = await supabase
        .from("final_decision")
        .insert({
          applicant_id: id,
          admin_email: user.email,
          decision: pendingFinalDecision,
        });

      if (finalDecisionError) {
        setError(
          `Failed to record final decision: ${finalDecisionError.message}`
        );
        return;
      }

      // Send rejection email if decision is final_reject
      if (pendingFinalDecision === "final_reject") {
        try {
          // Get current user's session token
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) {
            return;
          }

          const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-rejection-email`, {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              applicant_id: id,
              name: applicant.full_name,
              email: applicant.email
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
          }
        } catch (error) {
        }
      }

      // Send acceptance email if decision is final_accept
      if (pendingFinalDecision === "final_accept") {
        try {
          // Get current user's session token
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) {
            return;
          }

          const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-acceptance-email`, {
            method: "POST",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              applicant_id: id,
              name: applicant.full_name,
              email: applicant.email
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
          }
        } catch (error) {
        }
      }

      // Update both local and global state
      setFinalDecision(pendingFinalDecision);
      setGlobalFinalDecision(pendingFinalDecision);
      setFinalDecisionMadeBy(user.email);
      setShowConfirmModal(false);
      setPendingFinalDecision("");

      // Refresh final decisions
      fetchFinalDecisions();
    } catch (error) {
      setError("Failed to update final decision");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelFinalDecision = () => {
    setShowConfirmModal(false);
    setPendingFinalDecision("");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "reviewed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "accepted":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getFinalDecisionColor = (decision) => {
    switch (decision) {
      case "final_accept":
        return "bg-green-200 text-green-900 border-green-300";
      case "final_reject":
        return "bg-red-200 text-red-900 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const statuses = ["pending", "reviewed", "accepted", "rejected"];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aifund mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading applicant...
          </p>
        </div>
      </div>
    );
  }

  if (error || !applicant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {error || "Applicant not found"}
          </h2>
          <button
            onClick={navigateToDashboard}
            className="bg-aifund text-white px-6 py-3 rounded-md hover:bg-aifund-dark transition-colors duration-200"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <button
                  onClick={navigateToDashboard}
                  className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
                >
                  Dashboard
                </button>
                <span>›</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {applicant.full_name}
                </span>
                {hasNavContext && (
                  <>
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {currentIndex + 1} of {totalCount}
                    </span>
                  </>
                )}
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Welcome, {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Applicant Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {applicant.full_name}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {applicant.role} • {applicant.professional_experience}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Applied on {formatDate(applicant.created_at)}
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex flex-col items-start md:items-end space-y-3">
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Status:
                </label>
                <select
                  value={applicant.status}
                  onChange={(e) => updateApplicantStatus(e.target.value)}
                  disabled={updating}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Final Decision:
                </label>
                <select
                  value={globalFinalDecision || finalDecision}
                  onChange={(e) => {
                    const decision = e.target.value;
                    if (
                      decision === "final_accept" ||
                      decision === "final_reject"
                    ) {
                      setPendingFinalDecision(decision);
                      setShowConfirmModal(true);
                    } else {
                      setFinalDecision(decision);
                    }
                  }}
                  disabled={updating || globalFinalDecision !== ""}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {globalFinalDecision === "" ? (
                    <>
                      <option value="">No Final Decision</option>
                      <option value="final_accept">Final Accept</option>
                      <option value="final_reject">Final Reject</option>
                    </>
                  ) : (
                    <option value={globalFinalDecision}>
                      {globalFinalDecision === "final_accept"
                        ? "Final Accept"
                        : "Final Reject"}
                    </option>
                  )}
                </select>
              </div>

              <div className="flex flex-col items-end space-y-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                    applicant.status
                  )}`}
                >
                  {applicant.status.charAt(0).toUpperCase() +
                    applicant.status.slice(1)}
                </span>

                {globalFinalDecision && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${getFinalDecisionColor(
                      globalFinalDecision
                    )}`}
                  >
                    {globalFinalDecision === "final_accept"
                      ? "Final Accept"
                      : "Final Reject"}
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Profile Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Personal Info */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Personal Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Email
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.email}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Phone
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.phone}
                  </p>
                </div>
                {applicant.linkedin_profile && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      LinkedIn Profile
                    </label>
                    <a
                      href={applicant.linkedin_profile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {applicant.linkedin_profile}
                    </a>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Location
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.location || "Not specified"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    T-Shirt Size
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.tshirt_size}
                  </p>
                </div>
                {applicant.dietary_restrictions && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                      Dietary Restrictions
                    </label>
                    <p className="text-gray-900 dark:text-white">
                      {applicant.dietary_restrictions}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    How They Heard About Event
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.heard_about_event}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Professional Background
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Role/Position
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.role}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Professional Background
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.professional_background}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Years of Experience
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.professional_experience}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Team Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Team Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                    Team Participation
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {applicant.team_participation}
                  </p>
                </div>
                {applicant.team_members &&
                  applicant.team_members.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">
                        Team Members
                      </label>
                      <div className="mt-2 space-y-2">
                        {JSON.parse(applicant.team_members).map(
                          (member, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-700 rounded-md p-3"
                            >
                              <p className="font-medium text-gray-900 dark:text-white">
                                {member.name}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {member.email}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {member.role}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </motion.div>
          </div>

          {/* Right Column - Goals & Resume */}
          <div className="lg:col-span-2 space-y-6">
            {applicant.goals && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Event Goals
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {applicant.goals}
                </p>
              </motion.div>
            )}

            {applicant.ai_question && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.35 }}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Approximately how many hours have you spent coding with AI
                  assistance? Then, please briefly describe how you've
                  integrated AI into your workflow, including any specific
                  examples or challenges.
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {applicant.ai_question}
                </p>
              </motion.div>
            )}

            {applicant.resume_url && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Resume
                </h3>
                <ResumeDownloadButton
                  resumeUrl={applicant.resume_url}
                  resumeFilename={applicant.resume_filename}
                />
              </motion.div>
            )}

            {/* Admin Decisions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Admin Decisions
              </h3>
              {adminDecisions.length > 0 ? (
                <div className="space-y-2">
                  {adminDecisions.map((decision, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {decision.admin_email}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          decision.decision
                        )}`}
                      >
                        {decision.decision.charAt(0).toUpperCase() +
                          decision.decision.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No admin decisions recorded yet.
                </p>
              )}
            </motion.div>

            {/* Final Decisions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Final Decisions
              </h3>
              {finalDecisions.length > 0 ? (
                <div className="space-y-2">
                  {finalDecisions.map((decision, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md"
                    >
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {decision.admin_email}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getFinalDecisionColor(
                          decision.decision
                        )}`}
                      >
                        {decision.decision === "final_accept"
                          ? "Final Accept"
                          : "Final Reject"}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  No final decisions recorded yet.
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Final Decision Confirmation Modal */}
      <FinalDecisionModal
        isOpen={showConfirmModal}
        onClose={handleCancelFinalDecision}
        onConfirm={handleConfirmFinalDecision}
        decision={pendingFinalDecision}
        applicantName={applicant.full_name}
      />

      {/* Floating Navigation Arrows */}
      {hasNavContext && (
        <>
          {/* Previous Arrow - Left Side */}
          <button
            onClick={navigateToPrevious}
            disabled={!canNavigatePrevious}
            className={`fixed left-4 top-1/2 transform -translate-y-1/2 z-40 p-3 rounded-full shadow-lg border transition-all duration-200 ${
              canNavigatePrevious
                ? "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl"
                : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-600 cursor-not-allowed opacity-50"
            }`}
            title={canNavigatePrevious ? "Previous applicant" : "No previous applicant"}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Next Arrow - Right Side */}
          <button
            onClick={navigateToNext}
            disabled={!canNavigateNext}
            className={`fixed right-4 top-1/2 transform -translate-y-1/2 z-40 p-3 rounded-full shadow-lg border transition-all duration-200 ${
              canNavigateNext
                ? "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl"
                : "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-600 border-gray-200 dark:border-gray-600 cursor-not-allowed opacity-50"
            }`}
            title={canNavigateNext ? "Next applicant" : "No next applicant"}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default AdminApplicant;
